import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { AIButton } from "@similarweb/ai-kit";
import { AiSparkles } from "@similarweb/icons";
import type { ITableProps } from "@similarweb/pro-logic-components";
import { ExcelExport, Table } from "@similarweb/pro-logic-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { isAvailable } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import { translate } from "app/filters/i18nFilter";
import { WidgetBoxWithMarginBottom } from "app/pages/digital-marketing/ad-intelligence/common/StyledComponents";
import {
    AI_MODELS_SORT_ORDER,
    ALL_TOPICS_NAME,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMConsts";
import {
    getBaseWidgetRequestParams,
    navigateToRecommendations,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import type { ILLMReportWidget, LLModel } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { ReportStatus } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { TableWrapper } from "app/pages/digital-marketing/generative-ai/shared/styledComponents";
import { DefaultFetchService } from "app/services/fetchService";
import { allTrackers } from "app/services/track/track";

import { useTopicsSummaryData } from "../hooks/useTopicsSummaryData";

import { getColumnConfig } from "./config";
import { EXCEL_API_ENDPOINT, MAX_TABLE_ITEMS } from "./consts";
import { StickyTopicColumnWrapper } from "./TopicsSummaryComponent.styled";
import type { ITopicLlmBreakdown, ITopicVisibilityTableDataRaw } from "./types";

const fetchService = DefaultFetchService.getInstance();

const buildLlmChildren = (topicKey: string, breakdowns: ITopicLlmBreakdown[]): ITopicVisibilityTableDataRaw[] => {
    const sorted = [...breakdowns].sort((a, b) => {
        const indexA = AI_MODELS_SORT_ORDER.indexOf(a.llm as LLModel);
        const indexB = AI_MODELS_SORT_ORDER.indexOf(b.llm as LLModel);
        return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
    });

    return sorted.map((breakdown) => ({
        key: `${topicKey}-${breakdown.llm}`,
        topic: breakdown.llm,
        visibility: breakdown.visibility,
        visibilityChange: breakdown.visibilityChange,
        brandMentionShareByTopics: breakdown.brandMentionShare,
        brandMentionShareByTopicsChange: breakdown.brandMentionShareChange,
        averagePosition: breakdown.averagePosition,
        competitive: breakdown.competitive,
        topBrands: breakdown.topBrands ?? [],
        sourceTypes: breakdown.sourceTypes ?? [],
    }));
};

export const TopicsSummaryComponent = ({
    campaign,
    campaignConfig,
    reportStatus,
    reportDuration,
}: ILLMReportWidget) => {
    const {
        params: { topic: queryTopic },
    } = useCurrentStateAndParams();

    const selectedTopic = (queryTopic as string) ?? ALL_TOPICS_NAME;
    const canLoadData = reportStatus === ReportStatus.Ready && !!campaign;
    const hasCompetitiveSet = campaign?.competitors?.some((c) => c.brands?.length > 0);

    const baseParams = useMemo(
        () => getBaseWidgetRequestParams(campaign, campaignConfig, reportDuration),
        [campaign, campaignConfig, reportDuration],
    );

    const { data, isLoading: isLoadingData } = useTopicsSummaryData(baseParams, {
        enabled: canLoadData,
    });

    const tableData = useMemo<ITopicVisibilityTableDataRaw[]>(() => {
        const rawRows = (data?.data ?? []) as ITopicVisibilityTableDataRaw[];
        const allRows = rawRows.map((row, index) => {
            const key = `${row.topic}-${index}`;
            const breakdowns = row.llmBreakdown ?? [];
            return {
                ...row,
                key,
                ...(breakdowns.length > 0 && {
                    children: buildLlmChildren(key, breakdowns),
                }),
            };
        });

        return selectedTopic === ALL_TOPICS_NAME
            ? allRows
            : allRows.filter((row) => row.topic?.toLowerCase() === selectedTopic.toLowerCase());
    }, [data, selectedTopic]);

    const columnsConfig = getColumnConfig(campaign, hasCompetitiveSet);
    const shouldShowLoading = (isLoadingData || !campaign) && reportStatus !== ReportStatus.Error;

    const excelDownloadUrl = useMemo(() => {
        if (!baseParams.campaignGuid) {
            return "";
        }
        return `${EXCEL_API_ENDPOINT}?${fetchService.requestParams(baseParams)}`;
    }, [baseParams]);

    const trackExcelDownload = () => {
        allTrackers.dispatchTrackEvent("Download", "submit-ok", "Table/Topics Summary/Excel");
    };

    const handleRecommendationsClick = () => {
        navigateToRecommendations(campaign);
    };

    const handleExpand = (expanded: boolean) => {
        const action = expanded ? "Expand" : "Collapse";
        allTrackers.dispatchTrackEvent("Expand Collapse", "click", `Table/${action}`);
    };

    const expandableConfig: ITableProps<ITopicVisibilityTableDataRaw>["expandable"] = {
        expandIconColumnIndex: 0,
        expandRowByClick: true,
        onExpand: handleExpand,
    };

    return (
        <WidgetBoxWithMarginBottom data-automation="topics-visibility-widget">
            <TableWrapper>
                <StickyTopicColumnWrapper>
                    <Table
                        id="topics-visibility-table"
                        // Force remount when switching between campaigns with/without competitors:
                        // the column tree shape changes (flat columns vs grouped headers with children),
                        // and Table doesn't reconcile that structural change cleanly otherwise.
                        key={`tableCompetitors${hasCompetitiveSet}`}
                        data-automation="topics-visibility-table"
                        size="large"
                        sticky={false}
                        pagination={{
                            pageSize: MAX_TABLE_ITEMS,
                            total: tableData.length,
                        }}
                        columnConfig={columnsConfig}
                        data={tableData}
                        theme="white"
                        edgeColumnsPadding="lg"
                        isLoading={shouldShowLoading}
                        bordersConfig={{ header: hasCompetitiveSet, groupBorder: hasCompetitiveSet }}
                        loaderRowCount={MAX_TABLE_ITEMS}
                        expandable={expandableConfig}
                    >
                        <Table.TableTop borderBottom>
                            <Table.TableTop.Title
                                title={translate("llm.topics_summary.title")}
                                tooltip={translate("llm.topics_summary.tooltip")}
                                extensions={
                                    <Flex
                                        alignItems="center"
                                        gap="8px"
                                    >
                                        {isLoadingData ||
                                        !isAvailable(swSettings.components.GenaiRecommendations) ? null : (
                                            <AIButton
                                                label={translate("llm.topics_summary.buttons.view.gap.analysis")}
                                                variant="primary"
                                                icon={<AiSparkles />}
                                                onClick={handleRecommendationsClick}
                                            />
                                        )}
                                        <ExcelExport
                                            excelDownloadUrl={excelDownloadUrl}
                                            onExcelDownloaded={trackExcelDownload}
                                        />
                                    </Flex>
                                }
                            />
                        </Table.TableTop>
                    </Table>
                </StickyTopicColumnWrapper>
            </TableWrapper>
        </WidgetBoxWithMarginBottom>
    );
};
